import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TurnSlots } from "../thread/slots";

/** `term-block`, in the terminal register — preformatted text on a sunken
 * surface, and no syntax highlighting.
 *
 * The native skin binds `std-codemirror` read-only to get highlighting for
 * free. This deliberately does not. A terminal transcript's code block is the
 * literal bytes a program produced, and colouring them by a guessed grammar
 * asserts something the producer did not: that this text IS the language the
 * caller named. Where a real terminal colours output it is because the
 * program emitted the escapes itself.
 *
 * `language` is therefore accepted and ignored, which is a deliberate,
 * documented no-op rather than an oversight. The contract carries it because
 * the native skin can use it; a skin is allowed to know less. */
@component({ name: "std-term-turn-term-block" })
export class TermTurnTermBlock extends LonaWebComponent {
  private $text: HTMLElement = this.$("text");

  static makeWith(props: TurnSlots.Props["term-block"]): TermTurnTermBlock {
    return (TermTurnTermBlock.make() as TermTurnTermBlock).bind(props);
  }

  bind(props: TurnSlots.Props["term-block"]): this {
    this.$text.textContent = props.text;
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: var(--turn-term-size, 12px);
        line-height: var(--turn-term-leading, 1.7);
      }
      #text {
        margin: 0;
        padding: 8px 10px;
        background: var(--surface-sunken, color-mix(in srgb, var(--text-pri, #1f2328) 4%, transparent));
        border-radius: 4px;
        color: var(--text-pri, #1f2328);
        /* Output does not reflow: a wrapped column breaks the alignment the
           producer wrote, so it scrolls instead. */
        white-space: pre;
        overflow-x: auto;
        max-height: 24em;
        overflow-y: auto;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`<pre id="text"></pre>`;
}
